import axios from 'axios'
import Config from 'config'

// axios can't read a response while it is still arriving, so the AI
// endpoints are called with fetch, carrying the same auth and workspace
// headers as the app's axios requests.
const appHeaders = () => {
  const { Authorization, Workspace } = axios.defaults.headers.common
  return {
    ...(Authorization ? { Authorization } : {}),
    ...(Workspace ? { Workspace: String(Workspace) } : {})
  }
}

const errorMessage = async (response) => {
  if (response.status === 429) {
    return 'Too many AI requests. Please wait a little and try again.'
  }
  try {
    const data = await response.json()
    if (data.detail) {
      return data.detail
    }
    const first = Object.values(data)[0]
    return Array.isArray(first) ? String(first[0]) : String(first)
  } catch (error) {
    return 'The AI assistant is unavailable.'
  }
}

// POSTs `body` and calls `onEvent` for each event of the newline-delimited
// JSON stream (see vislaw/ai/editor.py). Resolves when the stream ends and
// rejects on network and HTTP errors; errors reported inside the stream
// arrive as { type: 'error' } events.
const streamEvents = async (path, body, onEvent, signal) => {
  const response = await fetch(`${Config.network.apiUrl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...appHeaders() },
    body: JSON.stringify(body),
    signal
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response))
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const flush = (text) => {
    const lines = text.split('\n')
    lines.filter(line => line.trim()).forEach(line => onEvent(JSON.parse(line)))
  }
  for (;;) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }
    buffer += decoder.decode(value, { stream: true })
    const end = buffer.lastIndexOf('\n')
    if (end >= 0) {
      flush(buffer.slice(0, end))
      buffer = buffer.slice(end + 1)
    }
  }
  flush(buffer + decoder.decode())
}

// Content for the inline prompt: rewrites `selection` (HTML) or, without
// one, writes new content between `before` and `after` (plain text)
export const streamWrite = ({ instruction, selection, before, after }, onEvent, signal) => (
  streamEvents('ai/editor/write/', { instruction, selection, before, after }, onEvent, signal)
)

// One chat turn; `blocks` is the document as [{ id, html }]
export const streamAgent = ({ history, request, blocks, selection }, onEvent, signal) => (
  streamEvents('ai/editor/agent/', { history, request, blocks, selection }, onEvent, signal)
)
